import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AnimatedDirective } from '../directives/animated.directive';
import { MatChipsModule } from '@angular/material/chips';
import { NgIf, NgStyle } from '@angular/common';

/**
 * Color configurations for status chips.
 * Used when color input is provided directly (without statuses array).
 *
 * bg/fg pairs are tuned for WCAG AA contrast (≥ 4.5:1) — `default` was
 * previously #E0E0E0 / #666666 (~4.15:1, borderline-failing AA) and is
 * now lightened to align with the very-light-bg / dark-fg pattern used
 * by the other variants.
 */
const STATUS_COLORS: Record<string, { bgColor: string; color: string }> = {
    default: { bgColor: '#F5F5F5', color: '#424242' },
    success: { bgColor: '#E8F5E9', color: '#2E7D32' },
    warn: { bgColor: '#FFF3E0', color: '#E65100' },
    info: { bgColor: '#E3F2FD', color: '#1565C0' },
    error: { bgColor: '#FFEBEE', color: '#C62828' },
};

@Component({
    selector: 'app-status-chip',
    templateUrl: './status-chip.component.html',
    styleUrls: ['./status-chip.component.scss'],
    standalone: true,
    imports: [
        AnimatedDirective,
        MatChipsModule,
        NgIf,
        NgStyle,
    ],
})
export class AppStatusChipComponent<T extends { id?: number; bgColor?: string; color?: string; title?: string }> implements OnChanges {
    @HostBinding('class') readonly hostClass = 'status-chip';

    @Input() statusId: number;
    @Input() statuses: T[];
    @Input() label: string;
    @Input() color: string; // 'default' | 'success' | 'warn' | 'info' | 'error'
    @Input() bgColor: string; // Custom background color (overrides named color)
    @Input() textColor: string; // Custom text color (overrides named color)
    @Input() loadingInitialData = false;

    @Output() statusSelected: EventEmitter<number> = new EventEmitter<number>();

    chip: T | { bgColor: string; color: string; title: string };

    /** True when the chip fell back to the neutral default color (no real status color) —
     *  drives a pale-gray border so the near-white default is still visible on a white card. */
    isDefaultChip = false;

    ngOnChanges(changes: SimpleChanges) {
        if (changes) {
            this.refreshChip();
        }
    }

    private refreshChip() {
        // If statuses array is provided, find by statusId (existing behavior)
        if (this.statuses?.length > 0) {
            this.chip = this.statuses.find((status) => {
                return status?.id === this.statusId;
            });
            this.isDefaultChip = false;
        }
        // Otherwise, if label is provided, create chip from label and color inputs
        else if (this.label) {
            // Custom bgColor/textColor take precedence over named color presets
            if (this.bgColor && this.textColor) {
                this.chip = {
                    bgColor: this.bgColor,
                    color: this.textColor,
                    title: this.label,
                };
                this.isDefaultChip = false;
            } else {
                const namedColor = STATUS_COLORS[this.color];
                const colorConfig = namedColor || STATUS_COLORS['default'];
                // Fell back to the neutral default (status has no real color) → flag for the border.
                this.isDefaultChip = !namedColor;
                this.chip = {
                    bgColor: colorConfig.bgColor,
                    color: colorConfig.color,
                    title: this.label,
                };
            }
        }
        // No valid input - chip will be undefined, showing "No Status"
        else {
            this.chip = undefined;
            this.isDefaultChip = false;
        }
    }
}
